import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, Clock, Zap, Play, Save, Sparkles, ChevronDown } from "lucide-react";
import { EmailPreviewButton } from "@/components/EmailPreviewModal";

/** Default rules (mirror of backend PRE_REMINDER_KEYS + POST) */
const RULES = [
  { key: "reminder_24h", label: "24-hour reminder", position: "before", defaultOffset: 24, unit: "hours" },
  { key: "reminder_1h", label: "1-hour reminder", position: "before", defaultOffset: 1, unit: "hours" },
  { key: "reminder_10min", label: "10-minute last-call", position: "before", defaultOffset: 10, unit: "minutes" },
  { key: "missed_it", label: "Missed-it (no-shows)", position: "after", defaultOffset: 5, unit: "minutes" },
  { key: "thank_you", label: "Thank you (attendees)", position: "after", defaultOffset: 5, unit: "minutes" },
];

function defaultOverridesFor(rule) {
  return {
    enabled: true,
    channels: ["email", "whatsapp"],
    [rule.unit === "hours" ? "offset_hours" : "offset_minutes"]: rule.defaultOffset,
  };
}

export default function AutomationBuilder({ w, msgs, scheduled, onReload }) {
  const initial = useMemo(() => {
    const o = w.automation_overrides || {};
    const result = {};
    for (const r of RULES) result[r.key] = { ...defaultOverridesFor(r), ...(o[r.key] || {}) };
    return result;
  }, [w.id]); // eslint-disable-line

  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const dirty = useMemo(() => JSON.stringify(state) !== JSON.stringify(initial), [state, initial]);

  const countsByKey = (scheduled.items || []).reduce((acc, r) => {
    acc[r.key] = acc[r.key] || { pending: 0, sent: 0, skipped: 0, failed: 0 };
    acc[r.key][r.status] = (acc[r.key][r.status] || 0) + 1;
    return acc;
  }, {});

  const patch = (key, upd) => setState((prev) => ({ ...prev, [key]: { ...prev[key], ...upd } }));

  const toggleChannel = (key, ch) => {
    const cur = state[key].channels || [];
    const next = cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch];
    patch(key, { channels: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/webinars/${w.id}`, { automation_overrides: state });
      toast.success("Automation saved. New registrations will use this timeline.");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const fireNow = async (key) => {
    try {
      const { data } = await api.post(`/webinars/${w.id}/fire-now/${key}`);
      toast.success(`Fired ${data.fired} message${data.fired === 1 ? "" : "s"}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const totals = scheduled.counts || {};

  return (
    <div className="space-y-6">
      {/* Header + KPI + Save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-3 flex-wrap">
          <MiniStat label="Pending" value={totals.pending ?? 0} tint="#0B5CFF" />
          <MiniStat label="Sent" value={totals.sent ?? 0} tint="#1E8E3E" />
          <MiniStat label="Skipped" value={totals.skipped ?? 0} tint="#747680" />
          <MiniStat label="Failed" value={totals.failed ?? 0} tint="#E02D3C" />
        </div>
        <button onClick={save} disabled={!dirty || saving} data-testid="save-automation-btn"
          className="vp-btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
        </button>
      </div>

      {/* Timeline */}
      <div className="vp-card p-6 sm:p-8 relative">
        <div className="vp-label mb-1">Automation timeline</div>
        <div className="font-display text-2xl font-semibold mb-6">Registration → live → after</div>

        <div className="relative pl-10">
          {/* vertical line */}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-[#C8CCD6]" />

          {/* Anchor: Registration complete */}
          <Anchor label="Registration complete" tint="#0B5CFF" caption="Confirmation always sent" />

          {/* Pre-webinar rules */}
          {RULES.filter((r) => r.position === "before").map((r) => (
            <RuleRow
              key={r.key} rule={r} state={state[r.key]} w={w}
              counts={countsByKey[r.key] || {}}
              expanded={expanded === r.key}
              setExpanded={() => setExpanded(expanded === r.key ? null : r.key)}
              patch={(u) => patch(r.key, u)}
              toggleChannel={(ch) => toggleChannel(r.key, ch)}
              fireNow={() => fireNow(r.key)}
            />
          ))}

          {/* Live anchor */}
          <Anchor label="Webinar goes live" tint="#E02D3C" caption={`Duration: ${w.duration_min} min`} />

          {/* Post-webinar rules */}
          {RULES.filter((r) => r.position === "after").map((r) => (
            <RuleRow
              key={r.key} rule={r} state={state[r.key]} w={w}
              counts={countsByKey[r.key] || {}}
              expanded={expanded === r.key}
              setExpanded={() => setExpanded(expanded === r.key ? null : r.key)}
              patch={(u) => patch(r.key, u)}
              toggleChannel={(ch) => toggleChannel(r.key, ch)}
              fireNow={() => fireNow(r.key)}
            />
          ))}

          {/* Terminal anchor */}
          <Anchor label="Day 1 · 3 · 7 nurture" tint="#747680" caption="Ships in phase 2" muted />
        </div>
      </div>
    </div>
  );
}

function Anchor({ label, tint, caption, muted }) {
  return (
    <div className="relative flex items-start gap-4 mb-6">
      <div className="absolute -left-[38px] top-1 w-6 h-6 rounded-full flex items-center justify-center"
           style={{ background: tint, opacity: muted ? 0.4 : 1 }}>
        <Sparkles className="w-3 h-3 text-white" strokeWidth={2} />
      </div>
      <div>
        <div className="font-display font-semibold text-lg" style={{ opacity: muted ? 0.5 : 1 }}>{label}</div>
        <div className="text-xs text-[#747680] mt-0.5">{caption}</div>
      </div>
    </div>
  );
}

function RuleRow({ rule, state, w, counts, expanded, setExpanded, patch, toggleChannel, fireNow }) {
  const enabled = state.enabled !== false;
  const chE = (state.channels || []).includes("email");
  const chW = (state.channels || []).includes("whatsapp");
  const offset = state.offset_hours ?? state.offset_minutes ?? rule.defaultOffset;
  const unit = rule.unit;
  const isPre = rule.position === "before";

  return (
    <div className="relative mb-4">
      <div className="absolute -left-[34px] top-4 w-4 h-4 rounded-full border-2 border-[#FFFFFF]"
           style={{ background: enabled ? "#0B5CFF" : "#C8CCD6", boxShadow: enabled ? "0 0 0 2px #0B5CFF" : "0 0 0 1px #C8CCD6" }} />

      <div className={"vp-card p-5 transition-opacity duration-200 " + (enabled ? "" : "opacity-60")}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold text-lg">{rule.label}</span>
              {counts.pending > 0 && <span className="vp-chip vp-chip-accent">{counts.pending} pending</span>}
              {counts.sent > 0 && <span className="vp-chip" style={{ color: "#1E8E3E", borderColor: "#1E8E3E60" }}>{counts.sent} sent</span>}
            </div>
            <div className="text-xs text-[#747680] mt-1 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              Fires <b className="text-[#0B0D17] font-semibold">{offset} {unit}</b> {isPre ? "before start" : "after end"} · via {(state.channels || []).join(" + ") || "no channels"}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* channel toggles */}
            <button
              onClick={() => toggleChannel("email")}
              className={"vp-chip transition-colors duration-150 " + (chE ? "vp-chip-accent" : "")}
              data-testid={`ch-email-${rule.key}`}
              title="Email"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Email
            </button>
            <button
              onClick={() => toggleChannel("whatsapp")}
              className={"vp-chip transition-colors duration-150 " + (chW ? "vp-chip-accent" : "")}
              data-testid={`ch-whatsapp-${rule.key}`}
              title="WhatsApp"
            >
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
            </button>
            {/* enable switch */}
            <button
              onClick={() => patch({ enabled: !enabled })}
              data-testid={`toggle-${rule.key}`}
              className={"vp-chip transition-colors duration-150 " + (enabled ? "vp-chip-accent" : "")}
            >
              {enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
        </div>

        <button onClick={setExpanded} className="mt-3 text-xs text-[#0B5CFF] inline-flex items-center gap-1 hover:underline">
          <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (expanded ? "rotate-180" : "")} /> {expanded ? "Hide details" : "Edit timing & template"}
        </button>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-[#E4E6EC] grid md:grid-cols-3 gap-4">
            <div>
              <div className="vp-label mb-1.5">Fire offset ({unit})</div>
              <input
                type="number" min="0" className="vp-input"
                value={offset}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  patch(unit === "hours" ? { offset_hours: v } : { offset_minutes: v });
                }}
                data-testid={`offset-${rule.key}`}
              />
            </div>
            <div className="md:col-span-2">
              <div className="vp-label mb-1.5">Preview body (from templates)</div>
              <div className="vp-input min-h-[80px] text-xs whitespace-pre-wrap text-[#4A4D57]">
                {w.email_templates?.[rule.key]?.body || w.whatsapp_templates?.[rule.key] || "(no template)"}
              </div>
              <div className="text-xs text-[#747680] mt-1.5">Edit the actual template body from the <b>Content</b> tab.</div>
            </div>
            <div className="md:col-span-3 flex items-center gap-2 flex-wrap">
              {w.email_templates?.[rule.key] && (
                <EmailPreviewButton
                  template={w.email_templates[rule.key]}
                  ruleName={rule.key}
                  hostName={w.owner_name || "Your host"}
                />
              )}
              {counts.pending > 0 && (
                <button onClick={fireNow} data-testid={`fire-now-${rule.key}`}
                  className="vp-btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1">
                  <Play className="w-3 h-3" /> Fire the {counts.pending} pending now (demo)
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  return (
    <div className="vp-card px-4 py-3 flex items-center gap-3">
      <div className="w-2 h-2 rounded-full" style={{ background: tint }} />
      <div>
        <div className="font-display text-xl font-semibold leading-none" style={{ color: tint }}>{value}</div>
        <div className="vp-label mt-1" style={{ fontSize: "0.6rem" }}>{label}</div>
      </div>
    </div>
  );
}
